import { print } from 'graphql';
import { gql } from 'graphql-tag';
import type { RxCollection, RxDocument, RxJsonSchema } from 'rxdb';

import { getDateModified } from '../../utilities/utilityFunctions';
import { RiskAssessmentDocument } from '../riskAssessment/queryBuilder';
import { defaultDoc, getValueFromLocalStorage } from '../share/defaultDoc';

export interface ComponentAreaInfo {
  readonly componentFailureId: number;
  readonly areaId: number;
  readonly areaPcsId: string;
  readonly inspectionId: number;
  readonly trafficSafety: number;
}

export interface PlannedDeficiencyList {
  readonly assetPCSId: string;
  readonly assetId: number;
  readonly pkDeficiencyUUID: string;
  readonly repairFactorId: number;
  readonly isCompletedDuringInspection: boolean;
  readonly componentFailureId: string;
  readonly componentType: string;
  readonly pcsId: string;
  readonly remedialAction: string;
  readonly causeOfFailure: string;
  readonly remedialActionId: number;
  readonly commentRemedialAction: string;
  readonly assetCategoryComponentId: number;
  readonly commentAssetComponent: string;
  readonly assetInspectionId: number;
  readonly failureCauseCategory: number;
  readonly areaId: number;
  readonly isActive: boolean;
  readonly locationDescription: number;
  readonly commentObservation: string;
  readonly name: string;
  readonly dateObserved: string;
  readonly site: number;
  readonly discipline: number;
  readonly dateCreated: string;
  readonly dateModified: string;
  readonly isDelete: string;
  readonly fieldInspectorId: number;
  readonly leadInspectorId: number;
  readonly inspectionTaskId: number;
  readonly commentSiteCoordinator: string;
  readonly commentInspectorLead: string;
  readonly commentInspectorField: string;
  readonly attentionFlag: boolean;
  readonly taskStatusCategory: number;
  readonly componentAreaInfo: [ComponentAreaInfo];
  riskAssessment?: RiskAssessmentDocument[];
}

export const plannedDeficiencyListSchema: RxJsonSchema<PlannedDeficiencyList> = {
  type: 'object',
  version: 0,
  description: 'describes deficiency lists',
  primaryKey: 'pkDeficiencyUUID',
  properties: {
    assetPCSId: { type: 'string' },
    assetId: { type: ['number', 'null'] },
    pkDeficiencyUUID: { type: 'string', maxLength: 36 },
    componentFailureId: { type: 'string' },
    pcsId: { type: ['string', 'null'] },
    repairFactorId: { type: ['number', 'null'] },
    isCompletedDuringInspection: { type: ['boolean', 'null'] },
    componentType: { type: ['string', 'null'] },
    remedialAction: { type: ['string', 'null'] },
    causeOfFailure: { type: ['string', 'null'] },
    remedialActionId: { type: ['number', 'null'] },
    commentRemedialAction: { type: ['string', 'null'] },
    assetCategoryComponentId: { type: ['number', 'null'] },
    commentAssetComponent: { type: ['string', 'null'] },
    assetInspectionId: { type: ['number', 'null'] },
    failureCauseCategory: { type: ['number', 'null'] },
    areaId: { type: ['number', 'null'] },
    isActive: { type: ['boolean', 'null'] },
    locationDescription: { type: ['string', 'null'] },
    commentObservation: { type: ['string', 'null'] },
    name: { type: ['string', 'null'] },
    dateObserved: { type: ['string', 'null'] },
    site: { type: 'number' },
    discipline: { type: 'number' },
    dateCreated: { type: ['string', 'null'] },
    dateModified: { type: ['string', 'null'] },
    isDelete: { type: ['string', 'null'] },
    fieldInspectorId: { type: ['number', 'null'] },
    leadInspectorId: { type: ['number', 'null'] },
    inspectionTaskId: { type: ['number', 'null'] },
    commentSiteCoordinator: { type: ['string', 'null'] },
    commentInspectorLead: { type: ['string', 'null'] },
    commentInspectorField: { type: ['string', 'null'] },
    attentionFlag: { type: ['boolean', 'null'] },
    taskStatusCategory: { type: ['number', 'null'] },
    componentAreaInfo: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          componentFailureId: { type: ['number', 'null'] },
          areaId: { type: ['number', 'null'] },
          areaPcsId: { type: ['string', 'null'] },
          inspectionId: { type: ['number', 'null'] },
          trafficSafety: { type: ['number', 'null'] },
        },
      },
    },
  },
};

export type PlannedDeficiencyListCollection = RxCollection<PlannedDeficiencyList> | null;
export type PlannedDeficiencyListDocument = RxDocument<PlannedDeficiencyList>;

export const plannedDeficiencyListPullQueryBuilder = (document: PlannedDeficiencyList) => {
  let doc = document;
  if (!doc) {
    (doc as unknown) = { ...defaultDoc };
  }

  const query = print(gql`
    query plannedDeficiencyListFeed(
      $siteId: Int!
      $disciplineId: Int!
      $lastRequested: DateTime
      $isLeadInspector: Boolean!
    ) {
      plannedDeficiencyListFeed(
        siteId: $siteId
        disciplineId: $disciplineId
        lastRequested: $lastRequested
        isLeadInspector: $isLeadInspector
      ) {
        assetPCSId
        assetId
        pkDeficiencyUUID
        repairFactorId
        componentFailureId
        isCompletedDuringInspection
        componentType
        causeOfFailure
        remedialAction
        remedialActionId
        commentRemedialAction
        assetCategoryComponentId
        commentAssetComponent
        assetInspectionId
        failureCauseCategory
        areaId
        pcsId
        isActive
        locationDescription
        commentObservation
        name
        dateObserved
        site
        discipline
        dateCreated
        dateModified
        isDelete
        fieldInspectorId
        leadInspectorId
        inspectionTaskId
        commentSiteCoordinator
        commentInspectorLead
        commentInspectorField
        attentionFlag
        taskStatusCategory
        componentAreaInfo {
          componentFailureId
          areaId
          areaPcsId
          inspectionId
          trafficSafety
        }
      }
    }
  `);

  return {
    query,
    variables: {
      siteId: JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
      disciplineId: JSON.parse(getValueFromLocalStorage('selectedDiscipline'))
        ?.selectedDisciplineId,
      lastRequested: getDateModified(
        doc?.dateModified,
        doc?.dateCreated,
        doc.site,
        doc.discipline,
        JSON.parse(getValueFromLocalStorage('selectedSite'))?.selectedSiteId,
        JSON.parse(getValueFromLocalStorage('selectedDiscipline'))?.selectedDisciplineId,
      ),
      isLeadInspector: getValueFromLocalStorage('isLeadInspector') === 'True',
    },
  };
};

export default {};
